import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', default_persona: '' });

  useEffect(() => {
    api.get('/profile/')
      .then((res) => setProfile(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      setEditForm({ name: user.name || '', default_persona: user.default_persona });
    }
  }, [user]);

  const handleSaveUser = async () => {
    try {
      await updateUser(editForm);
      setEditing(false);
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      const res = await api.post('/profile/rebuild/');
      setProfile(res.data);
      toast.success('Profile rebuilt from conversations');
    } catch {
      toast.error('Failed to rebuild profile');
    } finally {
      setRebuilding(false);
    }
  };

  const handleProfileEdit = async (field, value) => {
    try {
      const res = await api.patch('/profile/', { [field]: value });
      setProfile(res.data);
      toast.success('Updated');
    } catch {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Profile</h1>

      {/* User info */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Account</h2>
          <button
            onClick={() => setEditing(!editing)}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
        {editing ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-transparent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Persona</label>
              <select
                value={editForm.default_persona}
                onChange={(e) => setEditForm({ ...editForm, default_persona: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-transparent transition-colors"
              >
                <option value="adaptive">Adaptive</option>
                <option value="brainstormer">Brainstormer</option>
                <option value="challenger">Challenger</option>
                <option value="devil">Devil's Advocate</option>
              </select>
            </div>
            <button
              onClick={handleSaveUser}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              Save
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Name:</span>
              <span className="ml-2 text-gray-900 dark:text-white">{user?.name || '-'}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Username:</span>
              <span className="ml-2 text-gray-900 dark:text-white">{user?.username}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Email:</span>
              <span className="ml-2 text-gray-900 dark:text-white">{user?.email}</span>
            </div>
            <div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Default Persona:</span>
              <span className="ml-2 text-gray-900 dark:text-white capitalize">{user?.default_persona}</span>
            </div>
          </div>
        )}
      </div>

      {/* AI Profile */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Profile</h2>
          <button
            onClick={handleRebuild}
            disabled={rebuilding}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {rebuilding ? 'Rebuilding...' : 'Rebuild from conversations'}
          </button>
        </div>

        {loading ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm">Loading...</p>
        ) : profile ? (
          <div className="space-y-4">
            {profile.interests?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Interests</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((i, idx) => (
                    <span key={idx} className="bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-400 px-2 py-1 rounded text-sm">
                      {i}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {profile.themes?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Themes</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.themes.map((t, idx) => (
                    <span key={idx} className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 px-2 py-1 rounded text-sm">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {profile.content_goals?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Content Goals</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.content_goals.map((g, idx) => (
                    <span key={idx} className="bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-400 px-2 py-1 rounded text-sm">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {profile.raw_observations?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Observations ({profile.raw_observations.length})
                </h3>
                <ul className="space-y-1">
                  {profile.raw_observations.slice(-5).map((obs, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                      {typeof obs === 'string' ? obs : obs.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-gray-400 dark:text-gray-500">Profile v{profile.version}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No profile data yet. Have some conversations and click "Rebuild" to generate your AI profile.
          </p>
        )}
      </div>

      <button
        onClick={logout}
        className="px-4 py-2 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900 transition-colors duration-200"
      >
        Sign out
      </button>
    </div>
  );
}
